// (2026-05-21) Orquestador del envio automatico de correo tras crear una factura.
//
// Reglas (espec del cliente):
//   1) Al generar CUALQUIER factura -> notificar automaticamente por correo.
//   2) Si la factura es a terceros (doc_tercero=true) -> ANTES de notificar la
//      nueva, procesar el batch de pendientes (notificacion_envio=false) del
//      emisor, para que SMTP intermitente no acumule deudas.
//   3) Para Separada (2 facturas: principal + comision) -> 2 correos separados,
//      uno por cada factura. El backend resuelve los destinatarios:
//        - Principal (doc_tercero=true): To=cliente+tercero, Cc=emisor
//        - Comision  (factura_tercero_id!=null): To=cliente,         Cc=emisor
//
// El flujo NO bloquea la UI: se dispara en background y muestra un toast
// con el resultado agregado (oks/fallos).

import {
  notificarFacturaEmail,
  getFacturasPendientesNotificacion,
} from "../controllers/api_invoices";
import { generateInvoicesPDFBase64 } from "./pdf/InvoicesPDF/generateInvoicesPDF";

/**
 * Genera el PDF de UNA factura y dispara la notificacion por correo.
 * Devuelve `{ id, exito, mensaje, destinatarios?, notificacion_envio }`.
 *
 * Si la generacion del PDF falla, devuelve exito=false sin pegarle al
 * endpoint del backend (no tendria sentido enviar correo sin adjunto).
 */
export async function notifyFacturaEmailWithPDF(facturaId) {
  if (!facturaId) {
    return { id: facturaId, exito: false, mensaje: "facturaId vacio" };
  }
  let pdfBase64;
  try {
    pdfBase64 = await generateInvoicesPDFBase64(facturaId);
  } catch (e) {
    return { id: facturaId, exito: false, mensaje: "Error generando PDF" };
  }
  if (!pdfBase64) {
    return {
      id: facturaId,
      exito: false,
      mensaje: "No se pudo generar PDF (sin items o error)",
    };
  }
  const r = await notificarFacturaEmail(facturaId, pdfBase64);
  return { id: facturaId, ...r };
}

/**
 * Procesa secuencialmente las facturas pendientes (notificacion_envio=false)
 * del emisor del JWT. Para cada una, regenera el PDF e intenta notificar.
 *
 * Devuelve un array de resultados (uno por factura procesada).
 */
export async function processBatchPendientes() {
  const data = await getFacturasPendientesNotificacion({ limit: 50 });
  const facturas = (data && data.facturas) || [];
  const results = [];
  for (const f of facturas) {
    // Ejecutar secuencial — evita saturar SMTP con bursts.
    // eslint-disable-next-line no-await-in-loop
    const res = await notifyFacturaEmailWithPDF(f.id);
    results.push(res);
  }
  return results;
}

/**
 * Disparado por el flujo de creacion de factura (proforma -> factura).
 * `facturaIds` puede tener:
 *   - 1 id (Combinada o factura normal)
 *   - 2 ids (Separada: principal + comision)
 *
 * `disparaBatch=true` -> primero procesa las pendientes del emisor y luego
 * notifica las nuevas. Recomendado dejar siempre en true para que SMTP
 * intermitente no deje cosas colgando.
 *
 * Retorna `{ pending: [...], current: [...] }` (resumen para mostrar al
 * usuario).
 */
export async function notifyAfterFacturaCreation(facturaIds, { disparaBatch = true } = {}) {
  const result = { pending: [], current: [] };
  if (disparaBatch) {
    try {
      result.pending = await processBatchPendientes();
    } catch (e) {
      console.warn("notifyAfterFacturaCreation: batch fallo", e);
    }
  }
  for (const id of (facturaIds || [])) {
    if (!id) continue;
    try {
      // eslint-disable-next-line no-await-in-loop
      const r = await notifyFacturaEmailWithPDF(id);
      result.current.push(r);
    } catch (e) {
      result.current.push({ id, exito: false, mensaje: e?.message || "Error" });
    }
  }
  return result;
}

/**
 * Helper para mostrar un toast resumen post-notificacion.
 * Usa react-toastify si esta disponible. Devuelve void.
 */
export function showNotificationSummary(result, toast) {
  if (!toast) return;
  const all = [...(result.pending || []), ...(result.current || [])];
  if (all.length === 0) return;
  const oks = all.filter((r) => r.exito).length;
  const fails = all.filter((r) => !r.exito);
  if (oks > 0) {
    toast.success(
      `Correo enviado para ${oks} factura(s).`,
      { autoClose: 3500 }
    );
  }
  if (fails.length) {
    const fallidas = fails.map((r) => r.id).filter(Boolean).join(", ");
    toast.warning(
      `${fails.length} factura(s) quedaron pendientes de envio` +
        (fallidas ? ` (id ${fallidas})` : "") +
        ". Se reintentaran en la proxima emision a tercero.",
      { autoClose: 5500 }
    );
  }
}
